import { useRef, useState } from "react";
import { Button } from "antd";
import { AimOutlined } from "@ant-design/icons";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import LocationAccuracyModal from "./LocationAccuracyModal";
import { UPDATE_PLACE_LOCATION } from "../graphql/queries";

const PLACE_GEO_FRAGMENT = gql`
  fragment PlaceGeoFragment on Place {
    id
    geo_location {
      latitude
      longitude
    }
  }
`;

interface PlaceOverviewProps {
  placeId: string;
  placeName?: string;
  fetchPlacesAgain?: () => void;
  buttonType?: "full" | "small";
  location?: { latitude: number; longitude: number } | null;
  /** V menu: zobrazit tlačítko „Zaměřit znovu“ i když už máme polohu (přezaměření) */
  showEvenWithLocation?: boolean;
}

export default function PlaceOverview({ placeId, placeName, fetchPlacesAgain, buttonType, location, showEvenWithLocation }: PlaceOverviewProps) {
  const apolloClient = useApolloClient();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitOnceRef = useRef(false);

  const eventId = localStorage.getItem("eventId") || "";

  const [updatePlaceLocation] = useMutation(UPDATE_PLACE_LOCATION);

  const handleSetLocation = async (coords: GeolocationCoordinates) => {
    if (loading || submitOnceRef.current) return;
    if (!placeId || !eventId) {
      console.error("PlaceOverview: chybí eventId nebo placeId");
      return;
    }
    setLoading(true);
    submitOnceRef.current = true;
    setShowModal(false);
    try {
      const { data, errors } = await updatePlaceLocation({
        variables: {
          input: {
            eventId,
            placeId,
            latitude: coords.latitude,
            longitude: coords.longitude,
          },
        },
      });

      if (errors?.length) {
        const msg = errors.map((e: { message?: string }) => e.message).join(" ");
        console.error("updatePlaceLocation", msg || "Chyba při ukládání polohy");
        return;
      }

      if (data?.updatePlaceLocation?.success) {
        // Best-effort: update cached Place geo_location so lists update without manual refresh.
        try {
          const cacheId =
            apolloClient.cache.identify({ __typename: "Place", id: placeId }) ||
            apolloClient.cache.identify({ __typename: "PlaceWithDistance", id: placeId });
          if (cacheId) {
            apolloClient.cache.writeFragment({
              id: cacheId,
              fragment: PLACE_GEO_FRAGMENT,
              data: {
                __typename: "Place",
                id: placeId,
                geo_location: {
                  __typename: "GeoLocation",
                  latitude: coords.latitude,
                  longitude: coords.longitude,
                },
              },
            });
          }
        } catch {
          // ignore cache write errors; network refetch will still fix it on return
        }
        if (fetchPlacesAgain) fetchPlacesAgain();
      } else {
        const backendMsg = data?.updatePlaceLocation?.message || "";
        const isNoRecords = /no records updated|žádný záznam|0 rows/i.test(backendMsg);
        const userMsg = isNoRecords
          ? "Poloha se nepodařila uložit (stánek nebyl v akci nalezen). Zkontrolujte, že máte vybranou správnou akci v menu."
          : backendMsg || "Chyba při ukládání polohy";
        console.error("updatePlaceLocation", userMsg);
      }
    } catch (err) {
      const msg = (err as Error)?.message || "Chyba při ukládání polohy";
      const isNoRecords = /no records updated|žádný záznam/i.test(msg);
      console.error("updatePlaceLocation", isNoRecords ? "no records / výběr akce" : msg);
    } finally {
      setLoading(false);
      setShowModal(false);
      submitOnceRef.current = false;
    }
  };

  // Na stránce místa bez polohy: skrýt tlačítko, když už máme location (full režim). V menu (showEvenWithLocation) vždy zobrazit pro přezaměření.
  if (location && !showEvenWithLocation) {
    return null;
  }

  return (
    <>
      <LocationAccuracyModal
        visible={showModal}
        onAccept={handleSetLocation}
        onAutoAccept={handleSetLocation}
        onCancel={() => setShowModal(false)}
        allowIpFallback={false}
      />
      {buttonType === "full" ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div
            style={{
              display: "flex",
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#1677ff',
              borderRadius: '50%',
              color: 'white',
              width: "50%",
              height: "25%",
              cursor: "pointer",
              textAlign: "center",
            }}
            onClick={() => setShowModal(true)}
          >
            <AimOutlined style={{ fontSize: '72px' }} />
            <h2 style={{ margin: 0 }}>Označit polohu stánku</h2>
            {placeName && <p style={{ margin: '8px 0 0', fontSize: 18, opacity: 0.9 }}>{placeName}</p>}
          </div>
        </div>
      ) : (
        <Button style={{ width: '100%', height: '50px', fontSize: '18px' }} type="primary" onClick={() => setShowModal(true)}>
          <AimOutlined style={{ fontSize: '24px' }} />Znovu zaměřit polohu
        </Button>
      )}
    </>
  );
}
